import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';
import {
  FaUserTie, FaEnvelope, FaPhone, FaIdCard,
  FaSave, FaPlus, FaSync, FaCalendarAlt,
  FaWallet, FaFileInvoiceDollar, FaBell,
  FaBan, FaCheckCircle, FaComment,
  FaExclamationTriangle, FaEdit,
} from 'react-icons/fa';
import './BillingSettingsTab.css';

// ─── helpers ──────────────────────────────────────────────────────────────────
const moneyBRL = (cents) =>
  (Number(cents || 0) / 100).toLocaleString('pt-BR', {
    style: 'currency', currency: 'BRL',
  });

const CYCLE_LABELS = {
  monthly: 'Mensal',
  quarterly: 'Trimestral',
  yearly: 'Anual',
};

const METHOD_LABELS = {
  boleto: 'Boleto',
  pix: 'PIX',
  both: 'Boleto + PIX',
};

const EMPTY_FORM = {
  billing_contact_id: null,
  responsavel_usuario_id: null,
  payment_method: 'boleto',
  billing_cycle: 'monthly',
  plan_value_cents: 0,
  plan_value_brl: '0,00',
  due_day: 10,
  pre_due_days: 3,
  notify_every_days: 2,
  block_after_days: 7,
  auto_suspend: true,
  auto_unsuspend: true,
  send_email: true,
  send_sms: false,
  comtele_route: '',
  billing_notes: '',
};

// ─── componente principal ──────────────────────────────────────────────────────
export default function BillingSettingsTab({ clienteId }) {
  const [loading, setLoading]     = useState(true);
  const [saving,  setSaving]      = useState(false);

  const [contacts,       setContacts]       = useState([]);
  const [billingContact, setBillingContact] = useState(null);
  const [asaasCustomerId, setAsaasCustomerId] = useState(null);

  const [form, setForm] = useState(EMPTY_FORM);

  // 'existing' | 'new'
  const [contactMode, setContactMode]             = useState('existing');
  const [showContactSelector, setShowContactSelector] = useState(false);

  const [newContact, setNewContact] = useState({
    nome: '', email: '', telefone: '', documento: '', cargo_tag: 'Financeiro',
  });

  // ── fetch ────────────────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/api/system-hub/billing/clients/${clienteId}/faturamento`
      );
      const s   = data?.settings;
      const cts = Array.isArray(data?.contacts) ? data.contacts : [];
      setContacts(cts);

      if (s) {
        const cents = Number(s.plan_value_cents || 0);
        setForm({
          billing_contact_id:      s.billing_contact_id || null,
          responsavel_usuario_id:  s.responsavel_usuario_id || null,
          payment_method:          s.payment_method  || 'boleto',
          billing_cycle:           s.billing_cycle   || 'monthly',
          plan_value_cents:        cents,
          plan_value_brl:          (cents / 100).toFixed(2).replace('.', ','),
          due_day:                 s.due_day             ?? 10,
          pre_due_days:            s.pre_due_days        ?? 3,
          notify_every_days:       s.notify_every_days   ?? 2,
          block_after_days:        s.block_after_days    ?? 7,
          auto_suspend:            !!s.auto_suspend,
          auto_unsuspend:          !!s.auto_unsuspend,
          send_email:              s.send_email !== undefined ? !!s.send_email : true,
          send_sms:                !!s.send_sms,
          comtele_route:           s.comtele_route || '',
          billing_notes:           s.billing_notes || '',
        });
        setAsaasCustomerId(s.asaas_customer_id || null);

        if (s.bc_id) {
          setBillingContact({
            id:        s.bc_id,
            nome:      s.bc_nome,
            email:     s.bc_email,
            telefone:  s.bc_telefone,
            documento: s.bc_documento,
            cargo_tag: s.bc_cargo_tag,
          });
        } else {
          setBillingContact(null);
        }
      }
    } catch {
      toast.error('Falha ao carregar configurações de faturamento.');
    } finally {
      setLoading(false);
    }
  }, [clienteId]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── form helpers ─────────────────────────────────────────────────────────
  const setField = (key, value) => setForm(f => ({ ...f, [key]: value }));

  const handleValueBRL = (raw) => {
    const normalized = raw.replace(/\./g, '').replace(',', '.').replace(/[^\d.]/g, '');
    const cents = Math.round(Number(normalized || 0) * 100);
    setForm(f => ({ ...f, plan_value_brl: raw, plan_value_cents: cents }));
  };

  const handleSelectContact = (contact) => {
    setBillingContact(contact);
    setForm(f => ({ ...f, billing_contact_id: contact.id }));
    setShowContactSelector(false);
  };

  // ── save ─────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!clienteId) return;

    if (contactMode === 'new' && (!newContact.nome || !newContact.email)) {
      toast.warn('Nome e e-mail do responsável financeiro são obrigatórios.');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        billing_contact_id:      form.billing_contact_id,
        responsavel_usuario_id:  form.responsavel_usuario_id || null,
        payment_method:          form.payment_method,
        billing_cycle:           form.billing_cycle,
        plan_value_cents:        form.plan_value_cents,
        due_day:                 Number(form.due_day),
        pre_due_days:            Number(form.pre_due_days),
        notify_every_days:       Number(form.notify_every_days),
        block_after_days:        Number(form.block_after_days),
        auto_suspend:            form.auto_suspend,
        auto_unsuspend:          form.auto_unsuspend,
        send_email:              form.send_email,
        send_sms:                form.send_sms,
        comtele_route:           form.comtele_route ? Number(form.comtele_route) : null,
        billing_notes:           form.billing_notes,
      };

      if (contactMode === 'new') {
        payload.new_contact = { ...newContact };
        payload.billing_contact_id = null;
      }

      await api.put(
        `/api/system-hub/billing/clients/${clienteId}/faturamento`,
        payload
      );
      toast.success('Configurações de faturamento salvas!');
      setContactMode('existing');
      setShowContactSelector(false);
      await loadData();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Falha ao salvar faturamento.');
    } finally {
      setSaving(false);
    }
  };

  // ── loading state ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="bst-loading">
        <div className="bst-skeleton bst-skeleton--xl" />
        <div className="bst-skeleton bst-skeleton--xl" />
        <div className="bst-skeleton bst-skeleton--lg" />
      </div>
    );
  }

  // ── derived ───────────────────────────────────────────────────────────────
  const sendDay   = Math.max(1, Number(form.due_day) - Number(form.pre_due_days));
  const blockDay  = `+${form.block_after_days}d`;

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="bst-wrap">

      {/* ── HEADER ────────────────────────────────────────────────── */}
      <div className="bst-top-bar">
        <div>
          <h3 className="bst-page-title">Faturamento</h3>
          <p className="bst-page-sub">
            Responsável financeiro, método de pagamento, régua de cobrança e
            integração Asaas.
          </p>
        </div>
        <button
          className="bst-btn bst-btn--primary"
          onClick={handleSave}
          disabled={saving}
        >
          <FaSave /> {saving ? 'Salvando…' : 'Salvar tudo'}
        </button>
      </div>

      <div className="bst-grid">

        {/* ╔══════════════════════════════════════════════════════════╗
            ║  COLUNA ESQUERDA                                         ║
            ╚══════════════════════════════════════════════════════════╝ */}
        <div className="bst-col">

          {/* ── Responsável Financeiro ──────────────────────────── */}
          <section className="bst-card">
            <div className="bst-card-head">
              <FaUserTie className="bst-card-icon" />
              <h4>Responsável Financeiro</h4>
            </div>

            {/* Contato atual */}
            {billingContact && contactMode === 'existing' ? (
              <div className="bst-contact-card">
                <div className="bst-avatar">
                  {(billingContact.nome || '?').charAt(0).toUpperCase()}
                </div>
                <div className="bst-contact-info">
                  <strong>{billingContact.nome}</strong>
                  <span className="bst-tag">{billingContact.cargo_tag || 'Financeiro'}</span>
                  <div className="bst-detail">
                    <FaEnvelope />{billingContact.email || <em>Sem e-mail</em>}
                  </div>
                  <div className="bst-detail">
                    <FaPhone />{billingContact.telefone || <em>Sem telefone</em>}
                  </div>
                  {billingContact.documento && (
                    <div className="bst-detail">
                      <FaIdCard />{billingContact.documento}
                    </div>
                  )}
                </div>
                <button
                  className="bst-icon-btn"
                  title="Trocar responsável"
                  onClick={() => setShowContactSelector(v => !v)}
                >
                  <FaEdit />
                </button>
              </div>
            ) : (
              !( contactMode === 'new') && (
                <div className="bst-info bst-info--warn">
                  <FaExclamationTriangle />
                  <div>
                    <strong>Nenhum responsável definido</strong>
                    <p>Selecione um contato existente ou cadastre um novo abaixo.</p>
                  </div>
                </div>
              )
            )}

            {/* Lista de seleção de contatos existentes */}
            {showContactSelector && contactMode === 'existing' && (
              <div className="bst-contact-selector">
                <p className="bst-micro-label">Selecionar contato existente:</p>
                {contacts.length === 0 ? (
                  <p className="bst-muted">Nenhum contato cadastrado para este cliente.</p>
                ) : contacts.map(c => (
                  <div
                    key={c.id}
                    className={`bst-contact-opt ${form.billing_contact_id === c.id ? 'active' : ''}`}
                    onClick={() => handleSelectContact(c)}
                  >
                    <div className="bst-avatar bst-avatar--sm">
                      {(c.nome || '?').charAt(0).toUpperCase()}
                    </div>
                    <div className="bst-contact-opt__meta">
                      <strong>{c.nome}</strong>
                      <span className="bst-muted">{c.email}</span>
                    </div>
                    {c.is_billing ? (
                      <span className="bst-tag bst-tag--green">Financeiro</span>
                    ) : null}
                  </div>
                ))}
              </div>
            )}

            {/* Toggle modo: existente / novo */}
            <div className="bst-mode-toggle">
              <button
                className={`bst-mode-btn ${contactMode === 'existing' ? 'active' : ''}`}
                onClick={() => { setContactMode('existing'); setShowContactSelector(false); }}
              >
                Contato existente
              </button>
              <button
                className={`bst-mode-btn ${contactMode === 'new' ? 'active' : ''}`}
                onClick={() => setContactMode('new')}
              >
                <FaPlus size={10} /> Novo contato
              </button>
            </div>

            {/* Formulário novo contato */}
            {contactMode === 'new' && (
              <div className="bst-new-contact">
                <div className="bst-field">
                  <label>Nome *</label>
                  <input
                    value={newContact.nome}
                    onChange={e => setNewContact(p => ({ ...p, nome: e.target.value }))}
                    placeholder="Nome completo"
                  />
                </div>
                <div className="bst-field">
                  <label>E-mail *</label>
                  <input
                    type="email"
                    value={newContact.email}
                    onChange={e => setNewContact(p => ({ ...p, email: e.target.value }))}
                    placeholder="financeiro@empresa.com"
                  />
                </div>
                <div className="bst-row2">
                  <div className="bst-field">
                    <label>Telefone</label>
                    <input
                      value={newContact.telefone}
                      onChange={e => setNewContact(p => ({ ...p, telefone: e.target.value }))}
                      placeholder="(11) 99999-9999"
                    />
                  </div>
                  <div className="bst-field">
                    <label>CPF / CNPJ</label>
                    <input
                      value={newContact.documento}
                      onChange={e => setNewContact(p => ({ ...p, documento: e.target.value }))}
                      placeholder="Apenas números"
                    />
                  </div>
                </div>
                <div className="bst-field">
                  <label>Cargo / Tag</label>
                  <input
                    value={newContact.cargo_tag}
                    onChange={e => setNewContact(p => ({ ...p, cargo_tag: e.target.value }))}
                    placeholder="Ex: Financeiro, Diretor"
                  />
                </div>
              </div>
            )}

            {/* Canais de notificação */}
            <div className="bst-divider" />
            <p className="bst-micro-label">Canais de notificação</p>
            <div className="bst-toggles">
              <label className="bst-toggle">
                <input
                  type="checkbox"
                  checked={form.send_email}
                  onChange={e => setField('send_email', e.target.checked)}
                />
                <span><FaEnvelope /> E-mail (Brevo)</span>
              </label>
              <label className="bst-toggle">
                <input
                  type="checkbox"
                  checked={form.send_sms}
                  onChange={e => setField('send_sms', e.target.checked)}
                />
                <span><FaPhone /> SMS (Comtele)</span>
              </label>
            </div>

            {form.send_sms && (
              <div className="bst-field" style={{ marginTop: 10 }}>
                <label>Rota Comtele (opcional)</label>
                <input
                  type="number"
                  value={form.comtele_route}
                  onChange={e => setField('comtele_route', e.target.value)}
                  placeholder="Padrão global se vazio"
                />
              </div>
            )}
          </section>

          {/* ── Integração Asaas ────────────────────────────────── */}
          <section className="bst-card">
            <div className="bst-card-head">
              <FaSync className="bst-card-icon" />
              <h4>Integração Asaas</h4>
            </div>

            <div className="bst-kv-row">
              <span className="bst-muted">Customer ID</span>
              <span className="bst-mono">
                {asaasCustomerId || '—'}
              </span>
            </div>

            {!asaasCustomerId ? (
              <div className="bst-info bst-info--blue" style={{ marginTop: 10 }}>
                <FaExclamationTriangle />
                <div>
                  <strong>Não sincronizado</strong>
                  <p>
                    O Customer ID é gerado automaticamente ao criar a primeira
                    cobrança para este cliente.
                  </p>
                </div>
              </div>
            ) : (
              <div className="bst-info bst-info--green" style={{ marginTop: 10 }}>
                <FaCheckCircle />
                <div>
                  <strong>Sincronizado</strong>
                  <p>Este cliente já possui um Customer registrado no Asaas.</p>
                </div>
              </div>
            )}
          </section>

          {/* ── Override avançado ───────────────────────────────── */}
          <section className="bst-card">
            <details>
              <summary className="bst-summary">
                ⚙ Substituição por usuário do sistema (avançado)
              </summary>
              <div style={{ marginTop: 10 }}>
                <div className="bst-field">
                  <label>ID do usuário responsável (usuarios.id)</label>
                  <input
                    value={form.responsavel_usuario_id || ''}
                    onChange={e => setField('responsavel_usuario_id', e.target.value || null)}
                    placeholder="Vazio = usa o contato financeiro acima"
                  />
                  <span className="bst-hint">
                    Sobrescreve o responsável para buscas no Asaas e notificações.
                  </span>
                </div>
              </div>
            </details>
          </section>

        </div>

        {/* ╔══════════════════════════════════════════════════════════╗
            ║  COLUNA DIREITA                                          ║
            ╚══════════════════════════════════════════════════════════╝ */}
        <div className="bst-col">

          {/* ── Plano & Pagamento ────────────────────────────────── */}
          <section className="bst-card">
            <div className="bst-card-head">
              <FaWallet className="bst-card-icon" />
              <h4>Plano &amp; Pagamento</h4>
            </div>

            <div className="bst-row2">
              <div className="bst-field">
                <label><FaWallet size={10} /> Valor do plano (R$)</label>
                <input
                  value={form.plan_value_brl}
                  onChange={e => handleValueBRL(e.target.value)}
                  placeholder="Ex: 199,90"
                />
                <span className="bst-hint">{moneyBRL(form.plan_value_cents)}</span>
              </div>
              <div className="bst-field">
                <label><FaCalendarAlt size={10} /> Ciclo de cobrança</label>
                <select
                  value={form.billing_cycle}
                  onChange={e => setField('billing_cycle', e.target.value)}
                >
                  {Object.entries(CYCLE_LABELS).map(([v, l]) => (
                    <option key={v} value={v}>{l}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="bst-field">
              <label><FaFileInvoiceDollar size={10} /> Método de cobrança padrão</label>
              <div className="bst-method-group">
                {Object.entries(METHOD_LABELS).map(([v, l]) => (
                  <button
                    key={v}
                    type="button"
                    className={`bst-method-btn ${form.payment_method === v ? 'active' : ''}`}
                    onClick={() => setField('payment_method', v)}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
          </section>

          {/* ── Régua de Cobrança ────────────────────────────────── */}
          <section className="bst-card">
            <div className="bst-card-head">
              <FaBell className="bst-card-icon" />
              <h4>Régua de Cobrança</h4>
            </div>

            <div className="bst-row2">
              <div className="bst-field">
                <label>Dia de vencimento</label>
                <input
                  type="number" min="1" max="28"
                  value={form.due_day}
                  onChange={e => setField('due_day', Number(e.target.value))}
                />
                <span className="bst-hint">Todo dia <b>{form.due_day}</b> do mês</span>
              </div>
              <div className="bst-field">
                <label>Avisar antes (dias)</label>
                <input
                  type="number" min="0" max="30"
                  value={form.pre_due_days}
                  onChange={e => setField('pre_due_days', Number(e.target.value))}
                />
                <span className="bst-hint">
                  1º envio: dia <b>{sendDay}</b>
                </span>
              </div>
            </div>

            <div className="bst-row2">
              <div className="bst-field">
                <label>Repetir aviso (dias)</label>
                <input
                  type="number" min="1" max="30"
                  value={form.notify_every_days}
                  onChange={e => setField('notify_every_days', Number(e.target.value))}
                />
                <span className="bst-hint">A cada {form.notify_every_days}d após vencimento</span>
              </div>
              <div className="bst-field">
                <label>Suspender após (dias)</label>
                <input
                  type="number" min="1" max="60"
                  value={form.block_after_days}
                  onChange={e => setField('block_after_days', Number(e.target.value))}
                />
                <span className="bst-hint">
                  Bloqueio: <b>{blockDay}</b> do vencimento
                </span>
              </div>
            </div>

            {/* Preview visual da régua */}
            <div className="bst-ruler">
              <span className="bst-micro-label" style={{ marginBottom: 8 }}>
                Linha do tempo (prévia)
              </span>
              <div className="bst-ruler-track">
                <div className="bst-ruler-node bst-ruler-node--blue">
                  <span className="bst-ruler-node__label">Envio</span>
                  <div className="bst-ruler-node__dot" />
                  <span className="bst-ruler-node__day">dia {sendDay}</span>
                </div>
                <div className="bst-ruler-line" />
                <div className="bst-ruler-node bst-ruler-node--yellow">
                  <span className="bst-ruler-node__label">Vencimento</span>
                  <div className="bst-ruler-node__dot" />
                  <span className="bst-ruler-node__day">dia {form.due_day}</span>
                </div>
                <div className="bst-ruler-line" />
                <div className="bst-ruler-node bst-ruler-node--red">
                  <span className="bst-ruler-node__label">Bloqueio</span>
                  <div className="bst-ruler-node__dot" />
                  <span className="bst-ruler-node__day">{blockDay}</span>
                </div>
              </div>
            </div>

            <div className="bst-divider" />

            <div className="bst-toggles bst-toggles--col">
              <label className="bst-toggle">
                <input
                  type="checkbox"
                  checked={form.auto_suspend}
                  onChange={e => setField('auto_suspend', e.target.checked)}
                />
                <span><FaBan /> Auto-suspender ao atingir limite de atraso</span>
              </label>
              <label className="bst-toggle">
                <input
                  type="checkbox"
                  checked={form.auto_unsuspend}
                  onChange={e => setField('auto_unsuspend', e.target.checked)}
                />
                <span><FaCheckCircle /> Auto-reativar ao confirmar pagamento</span>
              </label>
            </div>
          </section>

          {/* ── Observações Internas ─────────────────────────────── */}
          <section className="bst-card">
            <div className="bst-card-head">
              <FaComment className="bst-card-icon" />
              <h4>Observações Internas</h4>
            </div>
            <textarea
              className="bst-textarea"
              rows={4}
              value={form.billing_notes}
              onChange={e => setField('billing_notes', e.target.value)}
              placeholder="Acordos, negociações, histórico de inadimplência, observações gerais…"
              maxLength={2000}
            />
            <span className="bst-hint" style={{ textAlign: 'right', display: 'block' }}>
              {form.billing_notes.length}/2000
            </span>
          </section>

        </div>
      </div>
    </div>
  );
}